import { useState, useEffect, useRef } from 'react';
import { MdArrowBackIosNew, MdArrowForwardIos } from 'react-icons/md';
import axios from 'axios';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { useQuota } from '../../../context/QuotaContext';
import Breadcrumbs from '../../../components/Tools/Breadcrumbs';
import { Button } from '../../../ui/button';
import UsageLimits from '../../Protected/Tools/UsageLimits';
import { toast } from 'react-toastify';
import { Modal } from '../../../components/Modal/Modal';
import useThemeColors from '../../../hooks/useThemeColors';

// Import new components
import ChatSidebar from './components/ChatSidebar';
import MessageBubble from './components/MessageBubble';
import ChatInput from './components/ChatInput';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function AIGenerator() {
    const [chatHistory, setChatHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentChatId, setCurrentChatId] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [editingChatId, setEditingChatId] = useState(null);
    const [editingChatTitle, setEditingChatTitle] = useState('');
    const [gptLimitReached, setGptLimitReached] = useState(false);
    const [isVideoAnalyzing, setIsVideoAnalyzing] = useState(false);

    // Modal state variables
    const [isModalOpenHashtag, setIsModalOpenHashtag] = useState(false);
    const [isModalOpenCaption, setIsModalOpenCaption] = useState(false);
    const [isModalOpenTodo, setIsModalOpenTodo] = useState(false);
    const [isModalOpenUpload, setIsModalOpenUpload] = useState(false);
    const [templateData, setTemplateData] = useState(null);
    const [todoData, setTodoData] = useState(null);
    const [uploadImageData, setUploadImageData] = useState(null);

    const messagesContainerRef = useRef(null);
    const { activeWorkspace, idToken } = useWorkspace();
    const { quota, refreshQuota } = useQuota();
    const { isDark } = useThemeColors();

    // Check GPT limit from quota context
    useEffect(() => {
        if (quota?.gpt) {
            const isLimitReached = !quota.gpt.unlimited && quota.gpt.used >= quota.gpt.limit;
            setGptLimitReached(isLimitReached);
        }
    }, [quota]);

    // Set sidebar initial state based on screen size
    useEffect(() => {
        const handleResize = () => {
            setSidebarOpen(window.innerWidth >= 768);
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch conversation history on component mount
    useEffect(() => {
        if (activeWorkspace?.id && idToken) {
            fetchConversations();
        }
    }, [activeWorkspace, idToken]);

    // Fetch conversation details when currentChatId changes
    useEffect(() => {
        if (currentChatId && activeWorkspace?.id && idToken) {
            fetchConversationDetails(currentChatId);
        } else {
            setMessages([]);
        }
    }, [currentChatId, activeWorkspace, idToken]);

    // Scroll to bottom when messages change or on initial load
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Function to scroll the chat to the bottom
    const scrollToBottom = () => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    };

    // Close modal callback
    const closeModal = () => {
        setIsModalOpenHashtag(false);
        setIsModalOpenCaption(false);
        setIsModalOpenTodo(false);
        setIsModalOpenUpload(false);
        setTemplateData(null);
        setTodoData(null);
        setUploadImageData(null);
    };

    // Helper functions for modals
    const extractHashtags = (message) => {
        try {
            if (message.function_call && message.function_call.name === 'generateHashtags') {
                const args = JSON.parse(message.function_call.arguments);
                return args.hashtags || [];
            }
            if (message.content) {
                const hashtagRegex = /#[\w\d]+/g;
                return message.content.match(hashtagRegex) || [];
            }
            return [];
        } catch (err) {
            console.error('Error extracting hashtags:', err);
            return [];
        }
    };

    const extractCaption = (message) => {
        try {
            if (message.function_call && message.function_call.name === 'generateCaption') {
                const args = JSON.parse(message.function_call.arguments);
                return args.caption || '';
            }
            if (message.content) {
                if (message.content.toLowerCase().includes('caption:')) {
                    const captionStart = message.content.toLowerCase().indexOf('caption:') + 'caption:'.length;
                    const captionEnd = message.content.indexOf('\n', captionStart) > -1 ?
                        message.content.indexOf('\n', captionStart) : message.content.length;
                    return message.content.substring(captionStart, captionEnd).trim();
                }
            }
            return '';
        } catch (err) {
            console.error('Error extracting caption:', err);
            return '';
        }
    };

    const extractTodo = (message) => {
        try {
            if (message.function_call && message.function_call.name === 'generateTodo') {
                const args = JSON.parse(message.function_call.arguments);
                return {
                    task: args.task || '',
                    dueDate: args.dueDate ? new Date(args.dueDate) : null
                };
            }
            return null;
        } catch (err) {
            console.error('Error extracting todo:', err);
            return null;
        }
    };

    // Modal functions
    const openHashtagModal = (message) => {
        const hashtags = extractHashtags(message);
        if (hashtags.length === 0) {
            toast.error('No hashtags found in message');
            return;
        }
        setTemplateData({
            type: 'hashtag',
            data: hashtags.join(' ')
        });
        setIsModalOpenHashtag(true);
    };

    const openCaptionModal = (message) => {
        const caption = extractCaption(message);
        if (!caption) {
            toast.error('No caption found in message');
            return;
        }
        setTemplateData({
            type: 'caption',
            data: caption
        });
        setIsModalOpenCaption(true);
    };

    const openTodoModal = (message) => {
        const todoData = extractTodo(message);
        if (!todoData || !todoData.task) {
            toast.error('No valid task found in message');
            return;
        }
        setTodoData(todoData);
        setIsModalOpenTodo(true);
    };

    const extractPathFromGCSUrl = (url) => {
        try {
            // Extract the path from Google Cloud Storage URL
            // URL format: https://storage.googleapis.com/bucket-name/path/to/file
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/');
            
            // Remove the first empty element and the bucket name
            // pathParts[0] is empty, pathParts[1] is bucket name
            const pathWithoutBucket = pathParts.slice(2).join('/');
            
            return pathWithoutBucket;
        } catch (error) {
            console.error('Error extracting path from GCS URL:', error);
            return url; // Return original URL if parsing fails
        }
    };

    const openUploadModal = (generatedImage) => {
        const draftData = {
            posts: [{
                title: `AI Generated: ${generatedImage.prompt || 'Image'}`,
                description: `AI generated image: ${generatedImage.prompt || 'No description'}`,
                firebase_path: generatedImage.url, // This gets mapped to firebasePath in VideoUploader
                video_path: extractPathFromGCSUrl(generatedImage.url), // Extract folder pathname
                uploadType: 'post',
                uploadTo: 'draft',
                schedule: null,
                _id: `ai-generated-${Date.now()}`,
                post_id: `ai-generated-${Date.now()}`,
                platform_id: null
            }],
            status: 'draft',
            id: `ai-generated-${Date.now()}`
        };
        
        setUploadImageData(draftData);
        setIsModalOpenUpload(true);
    };

    const openVideoUrl = (videoUrl) => {
        window.open(videoUrl, '_blank');
    };

    const detectVideoUrls = (message) => {
        const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/g;
        return youtubeRegex.test(message);
    };

    const downloadImage = async (imageUrl, prompt) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            // Clean the prompt for filename
            const cleanPrompt = prompt ? prompt.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50) : 'generated_image';
            a.download = `${cleanPrompt}.png`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Image downloaded successfully!');
        } catch (error) {
            console.error('Error downloading image:', error);
            toast.error('Failed to download image');
        }
    };

    const handleGenerateImage = async (prompt) => {
        try {
            setLoading(true);
            
            const response = await axios.post(`${BACKEND_API_URL}/api/auth/user/ai/generate-image`, {
                prompt: prompt,
                workspaceId: activeWorkspace.id
            }, {
                headers: { Authorization: `Bearer ${idToken}` }
            });

            if (response.data.success) {
                // Add the generated image as a new message
                const imageMessage = {
                    role: 'assistant',
                    content: `I've generated an image for you: "${prompt}"`,
                    timestamp: new Date(),
                    generatedImage: response.data.image
                };

                // Add to current conversation
                if (currentChatId) {
                    await axios.post(`${BACKEND_API_URL}/api/auth/user/ai/conversations/${currentChatId}/messages`, {
                        message: `Generate image: ${prompt}`,
                        workspaceId: activeWorkspace.id
                    }, {
                        headers: { Authorization: `Bearer ${idToken}` }
                    });
                    
                    // Refresh conversation to show the new message
                    fetchConversationDetails(currentChatId);
                } else {
                    // Add to current messages if no conversation
                    setMessages(prev => [...prev, imageMessage]);
                }

                toast.success('Image generated successfully!');
                refreshQuota();
            }
        } catch (error) {
            console.error('Error generating image:', error);
            toast.error('Failed to generate image');
        } finally {
            setLoading(false);
        }
    };

    // Fetch all conversations
    const fetchConversations = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${BACKEND_API_URL}/api/auth/user/ai/conversations`, {
                params: { workspaceId: activeWorkspace.id },
                headers: { Authorization: `Bearer ${idToken}` }
            });

            if (response.data.success) {
                setChatHistory(response.data.conversations || []);
            }
        } catch (err) {
            console.error('Error fetching conversations:', err);
            setError('Failed to load conversations');
        } finally {
            setLoading(false);
        }
    };

    // Fetch details for a specific conversation
    const fetchConversationDetails = async (conversationId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${BACKEND_API_URL}/api/auth/user/ai/conversations/${conversationId}`, {
                params: { workspaceId: activeWorkspace.id },
                headers: { Authorization: `Bearer ${idToken}` }
            });

            if (response.data.success) {
                setMessages(response.data.conversation.messages || []);
            }
        } catch (err) {
            console.error('Error fetching conversation details:', err);
            setError('Failed to load conversation');
        } finally {
            setLoading(false);
        }
    };

    // Create a new conversation
    const handleNewChat = () => {
        setCurrentChatId(null);
        setMessages([]);
        setInputValue('');
    };

    // Send a message
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputValue.trim() || gptLimitReached) return;

        const userMessage = {
            role: 'user',
            content: inputValue,
            timestamp: new Date()
        };

        // Add user message to UI immediately
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);

        const tempInputValue = inputValue;
        setInputValue('');

        // Check if message contains video URLs
        const hasVideoUrl = detectVideoUrls(tempInputValue);

        // Set appropriate loading state
        if (hasVideoUrl) {
            setIsVideoAnalyzing(true);
        } else {
            setLoading(true);
        }

        try {
            if (currentChatId) {
                // Add message to existing conversation
                const response = await axios.post(`${BACKEND_API_URL}/api/auth/user/ai/conversations/${currentChatId}/messages`, {
                    message: tempInputValue,
                    workspaceId: activeWorkspace.id
                }, {
                    headers: { Authorization: `Bearer ${idToken}` }
                });

                // Refresh the conversation to get the updated messages
                fetchConversationDetails(currentChatId);
            } else {
                // Create a new conversation
                const response = await axios.post(`${BACKEND_API_URL}/api/auth/user/ai/conversations`, {
                    message: tempInputValue,
                    workspaceId: activeWorkspace.id
                }, {
                    headers: { Authorization: `Bearer ${idToken}` }
                });

                if (response.data.success) {
                    // Set the new conversation as current
                    setCurrentChatId(response.data.conversation.id);

                    // Refresh conversations list
                    fetchConversations();
                }
            }

            // Refresh quota after sending a message
            refreshQuota();
        } catch (err) {
            console.error('Error sending message:', err);
            setError('Failed to send message');
        } finally {
            // Reset both loading states
            setLoading(false);
            setIsVideoAnalyzing(false);
        }
    };

    const handleSelectChat = (id) => {
        setCurrentChatId(id);
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    // Delete a conversation
    const handleDeleteConversation = async (id) => {
        try {
            const response = await axios.delete(`${BACKEND_API_URL}/api/auth/user/ai/conversations/${id}`, {
                headers: { Authorization: `Bearer ${idToken}` }
            });

            if (response.data.success) {
                // Remove from state
                setChatHistory(prev => prev.filter(chat => chat.id !== id));

                // If deleted the current chat, clear it
                if (currentChatId === id) {
                    setCurrentChatId(null);
                    setMessages([]);
                }
            }
        } catch (err) {
            console.error('Error deleting conversation:', err);
            setError('Failed to delete conversation');
        }
    };

    // Delete all conversations
    const handleDeleteAllConversations = async () => {
        if (!window.confirm('Are you sure you want to delete all conversations? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await axios.delete(`${BACKEND_API_URL}/api/auth/user/ai/conversations`, {
                params: { workspaceId: activeWorkspace.id },
                headers: { Authorization: `Bearer ${idToken}` }
            });

            if (response.data.success) {
                // Clear all conversations from state
                setChatHistory([]);
                setCurrentChatId(null);
                setMessages([]);
                toast.success('All conversations deleted successfully');
            }
        } catch (err) {
            console.error('Error deleting all conversations:', err);
            setError('Failed to delete all conversations');
            toast.error('Failed to delete all conversations');
        }
    };

    // Start editing conversation title
    const handleEditConversation = (chat) => {
        setEditingChatId(chat.id);
        setEditingChatTitle(chat.title || chat.preview || '');
    };

    // Save updated conversation title
    const handleSaveConversationTitle = async (e) => {
        e.preventDefault();

        if (!editingChatTitle.trim()) {
            setEditingChatId(null);
            return;
        }

        try {
            const response = await axios.patch(`${BACKEND_API_URL}/api/auth/user/ai/conversations/${editingChatId}`, {
                title: editingChatTitle
            }, {
                headers: { Authorization: `Bearer ${idToken}` }
            });

            if (response.data.success) {
                // Update in state
                setChatHistory(prev => prev.map(chat =>
                    chat.id === editingChatId
                        ? { ...chat, title: editingChatTitle }
                        : chat
                ));
            }
        } catch (err) {
            console.error('Error updating conversation title:', err);
            setError('Failed to update conversation title');
        } finally {
            setEditingChatId(null);
        }
    };

    // Cancel editing conversation title
    const handleCancelEdit = () => {
        setEditingChatId(null);
    };

    // Error message display
    const renderError = () => {
        if (!error) return null;

        return (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
            </div>
        );
    };

    // Loading indicator
    const renderLoading = () => {
        if (!loading) return null;

        return (
            <div className="flex justify-center my-4">
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 rounded-full border-t-transparent"></div>
            </div>
        );
    };

    // Empty state for no conversations
    const renderEmptyState = () => {
        if (chatHistory.length > 0) return null;

        return (
            <div className="flex flex-col items-center justify-center p-8 text-center text-gray-500">
                <p className="mb-2">No conversations yet</p>
                <p>Start a new chat to begin</p>
            </div>
        );
    };

    return (
        <div className="dashboard-container !px-0 flex flex-col h-screen">
            <div className="px-[20px] w-full mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                <Breadcrumbs />
                <div className="mt-2 sm:mt-0 sm:mr-4">
                    <UsageLimits
                        showGpt={true}
                        showImage={true}
                        showUpload={false}
                        variant="pill"
                    />
                </div>
            </div>
            <div className="flex flex-col md:flex-row flex-1 overflow-hidden relative">
                {/* Left sidebar - Chat history */}
                <ChatSidebar
                    sidebarOpen={sidebarOpen}
                    chatHistory={chatHistory}
                    currentChatId={currentChatId}
                    editingChatId={editingChatId}
                    editingChatTitle={editingChatTitle}
                    setEditingChatTitle={setEditingChatTitle}
                    gptLimitReached={gptLimitReached}
                    loading={loading}
                    isVideoAnalyzing={isVideoAnalyzing}
                    onNewChat={handleNewChat}
                    onSelectChat={handleSelectChat}
                    onEditConversation={handleEditConversation}
                    onSaveConversationTitle={handleSaveConversationTitle}
                    onCancelEdit={handleCancelEdit}
                    onDeleteConversation={handleDeleteConversation}
                    onDeleteAllConversations={handleDeleteAllConversations}
                    renderError={renderError}
                    renderEmptyState={renderEmptyState}
                />

                {/* Sidebar toggle button - always visible */}
                <Button
                    variant="ghost"
                    onClick={toggleSidebar}
                    className={`absolute --theme-colors top-12 z-20 p-2 rounded-md shadow-md border border-gray-200 text-gray-600 hover:text-blue-600 transition-all duration-300 ${sidebarOpen ? 'right-2 md:left-60 md:right-auto' : 'left-2'
                        }`}
                >
                    {sidebarOpen ? <MdArrowBackIosNew className="h-5 w-5" /> : <MdArrowForwardIos className="h-5 w-5" />}
                </Button>

                {/* Main chat area */}
                <div className="flex-1 flex flex-col h-full overflow-hidden w-full">
                    {messages.length > 0 ? (
                        /* Chat with messages and input at bottom */
                        <>
                            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto">
                                <div className="space-y-4 p-4">
                                    {messages.map((message, index) => (
                                        <MessageBubble
                                            key={index}
                                            message={message}
                                            index={index}
                                            openVideoUrl={openVideoUrl}
                                            openHashtagModal={openHashtagModal}
                                            openCaptionModal={openCaptionModal}
                                            openTodoModal={openTodoModal}
                                            downloadImage={downloadImage}
                                            onGenerateImage={handleGenerateImage}
                                            onUploadImage={openUploadModal}
                                        />
                                    ))}
                                    {(loading || isVideoAnalyzing) && (
                                        <div className="flex justify-start p-4">
                                            <div className="--theme-colors border --theme-colors-border p-3 rounded-lg max-w-[250px] flex items-center">
                                                <div className="text-sm mr-2 --theme-colors">
                                                    {isVideoAnalyzing ? 'Analyzing video content...' : 'AI is typing'}
                                                </div>
                                                <div className="flex space-x-1">
                                                    <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-white' : 'bg-gray-500'}`} style={{ animationDelay: '0ms' }}></div>
                                                    <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-white' : 'bg-gray-500'}`} style={{ animationDelay: '150ms' }}></div>
                                                    <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-white' : 'bg-gray-500'}`} style={{ animationDelay: '300ms' }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <ChatInput
                                inputValue={inputValue}
                                setInputValue={setInputValue}
                                onSendMessage={handleSendMessage}
                                loading={loading}
                                isVideoAnalyzing={isVideoAnalyzing}
                                gptLimitReached={gptLimitReached}
                                isEmptyChat={false}
                            />
                        </>
                    ) : (
                        <ChatInput
                            inputValue={inputValue}
                            setInputValue={setInputValue}
                            onSendMessage={handleSendMessage}
                            loading={loading}
                            isVideoAnalyzing={isVideoAnalyzing}
                            gptLimitReached={gptLimitReached}
                            isEmptyChat={true}
                        />
                    )}
                </div>
            </div>

            {/* Modals */}
            {isModalOpenHashtag && (
                <div className='flex-col items-center border-t-2 pt-3 absolute'>
                    <Modal open={isModalOpenHashtag} closeModal={closeModal} type={'TemplatesModal'} otherInfo={templateData && {
                        type: 'hashtag',
                        initialData: templateData.data
                    }} />
                </div>
            )}

            {isModalOpenCaption && (
                <div className='flex-col items-center border-t-2 pt-3 absolute'>
                    <Modal open={isModalOpenCaption} closeModal={closeModal} type={'TemplatesModal'} otherInfo={templateData && {
                        type: 'caption',
                        initialData: templateData.data
                    }} />
                </div>
            )}

            {isModalOpenTodo && (
                <div className='flex-col items-center border-t-2 pt-3 absolute'>
                    <Modal open={isModalOpenTodo} closeModal={closeModal} type={'TodoModal'} otherInfo={todoData} />
                </div>
            )}

            {isModalOpenUpload && (
                <div className='flex-col items-center border-t-2 pt-3 absolute'>
                    <Modal open={isModalOpenUpload} closeModal={closeModal} type={'VideoUploaderModal'} otherInfo={{ draft: uploadImageData }} />
                </div>
            )}
        </div>
    );
} 